/**
 * FrozenMapSnapshot: a static MapLibre GL map centered on the site location.
 *
 * The map is "frozen" at t=0 (the alert moment): interactive controls are
 * disabled, panning and zooming are off. It shows where activity was at the
 * time of the alert.
 *
 * Map features:
 * - Centered on the site's resolved coordinates (location_obj first, then
 *   the legacy JSONB location fallback) via resolveSiteCoords + useSiteDetail
 * - SOC Tactical Gold marker at the site center (was cyan pre-Phase 4)
 * - Tile source: https://tiles.openfreemap.org/styles/liberty (free, no token)
 * - Fallback: "Site location unknown" placeholder when site has no coordinates
 *
 * PRODUCTION NOTE: openfreemap.org is free but may rate-limit on high traffic.
 * For production deployments, consider self-hosting tiles in DO Spaces.
 *
 * MapLibre in jsdom: jsdom has no WebGL. Unit tests mock maplibre-gl entirely
 * via vi.mock('maplibre-gl'). Playwright E2E exercises the real map in Chrome.
 */
import { useEffect, useMemo, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useSiteDetail } from '@/features/now/queries';
import { env } from '@/lib/env';
import { resolveSiteCoords } from '@/lib/site-coords';
import { theme } from '@/design/tokens.css';
import * as styles from './FrozenMapSnapshot.css';

/** Props for FrozenMapSnapshot. */
interface Props {
  /** UUID of the owning site (used to fetch location via useSiteDetail). */
  siteId: string;
  /** Zone numeric ID where the alert occurred; currently unused (future: zone polygon). */
  zoneId: number | null;
  /** ISO 8601 alert fire timestamp; the map freezes at this moment. */
  alertTs: string;
}

/**
 * Static MapLibre map showing site location at the time of the alert.
 *
 * Fetches site detail to get lat/lng coordinates. If the site has no
 * configured location, shows a placeholder message instead of a map.
 *
 * @param props - siteId, zoneId, alertTs
 */
export function FrozenMapSnapshot({ siteId, zoneId: _zoneId, alertTs: _alertTs }: Props) {
  const { data: site } = useSiteDetail(siteId);
  const containerRef = useRef<HTMLDivElement>(null);

  // 2026-05-29 (#182): resolve the frozen-map center through the shared
  // resolver so the event snapshot honors BOTH the canonical location_obj
  // AND the legacy JSONB `location` fallback (this surface previously read
  // the JSONB `location` ONLY). Memoized on `site` so the map-building
  // effect below re-runs only when the site row changes, preserving the
  // original `[site]` dependency cadence.
  const coords = useMemo(() => resolveSiteCoords(site), [site]);

  useEffect(() => {
    if (!containerRef.current || !coords) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      // Vector style sourced from `env.MAP_VECTOR_STYLE_URL` so an air-gapped /
      // internal deploy can point the frozen snapshot at an internal tile
      // server via VITE_MAP_VECTOR_STYLE_URL. The env default equals the public
      // openfreemap "liberty" style below, so the default build is unchanged
      // (default == current).
      style: env.MAP_VECTOR_STYLE_URL,
      center: [coords.lng, coords.lat],
      zoom: 17,
      // Frozen map: no user interaction
      interactive: false,
      attributionControl: false,
    });

    // SOC Tactical Gold marker at the site location. Routes through the
    // theme token so the marker tracks the active mode (slightly deeper
    // gold in light mode for AAA contrast).
    new maplibregl.Marker({ color: theme.colors.accent.gold })
      .setLngLat([coords.lng, coords.lat])
      .addTo(map);

    return () => map.remove();
  }, [coords]);

  if (!coords) {
    return (
      <div className={styles.placeholder} data-testid="frozen-map-placeholder">
        Site location unknown
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={styles.mapContainer}
      data-testid="frozen-map"
      aria-label="Frozen map snapshot of site location at alert time"
    />
  );
}
